import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useHistory, useStepRoutePath } from '@shared/routing';
import { useMediaQuery } from 'react-responsive';
import { useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ApolloError } from 'apollo-boost';

import {
	getAvailableServices,
	getExistGdsServicesOrInsurances,
	getHasAvailableSeats,
	getHasRequestedOrderServices,
	getIsBooked,
	getIsPriceFreezeProductSelected,
	getIsReadonly,
	getIsRedirectFromMetasearch,
	getIsTravellersDataFilled,
	getIsOrderBlockedForPayment,
	getOrder,
	getOrderId,
	getPaymentIsRequired,
	getShowServicesBeforeBooking
} from '../../store/order/selectors';
import { OrderGoal, reachGoal } from '../../../analytics';
import { downloadPDF, MOBILE_MIN_WIDTH } from '../../../utils';
import { useConfig } from '../../../context';

import {
	OrderDocumentType,
	useOrderDocumentQuery,
	useResendItineraryReceiptMutation,
	OrderPromoCodeInfoFragment,
	OrderStatus,
	useUpdateOrderMutation,
	useOrderQuery,
	useResetOrderPaymentBlockMutation,
	PaymentStatusEnum,
	CheckoutOrderFragment,
	GdsServiceFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	useAdditionalServicesLazyQuery,
	NASProductFragment,
	NASProductType
} from '@websky/graphql';
import { useConditionalPolling, useToggleable } from '../../../hooks';
import { ResendTicketsStages } from '../../../ResendTicketsForm/types';
import { getSteps, removeStepsWhenOrderHasPassengersData, removeStepsWhenRedirectFromMetasearch } from '../../steps';
import { StepsEnum } from '../../../types';
import { analyzePurchasedProducts as analyzePurchasedProductsAction, fillOrder } from '../../store/order/actions';
import { getErrorMessage, NormalizedError } from '@shared/error';
import { useResetPaymentBlock, usePaymentPolling } from '@features/payment';
import { PaymentResultStatus, ServiceOpenEvent, Step, StepType } from '../../types';
import { WEBSKY_SERVICE_OPEN_EVENT } from '../../constants';
import { getQueryPopupGoal } from '../../../DataLayerAnalytics/utils';
import {
	CombinedSelectedServiceSubType,
	CombinedSelectedServiceType
} from '../../../AIPurchasePredictionService/types';
import { getQueryServicesParams, getUrlWithServicesParams } from '../../utils';

export class OrderIsArchivedTicketDownloadError extends Error {
	public constructor() {
		super('Your order was archived. Please contact with client support to get a receipt');

		Object.setPrototypeOf(this, OrderIsArchivedTicketDownloadError.prototype);
	}
}

export const useDownloadOrderDocument = () => {
	const order = useSelector(getOrder);
	const [_error, setError] = useState<Error>(null);
	const { isOpen: loading, open: setLoading, close: setNotLoading } = useToggleable(false);
	const { isOpen: warningIsOpen, open: warningOpen, close: warningClose } = useToggleable(false);

	const { refetch: refetchDocuments, data, error } = useOrderDocumentQuery({
		skip: true
	});

	const handleDownload = useCallback(
		async (passengerId?: string, documentType?: OrderDocumentType) => {
			if (loading) {
				return;
			}

			try {
				if (order.isArchived) {
					throw new OrderIsArchivedTicketDownloadError();
				}

				setLoading();

				reachGoal(OrderGoal.ItineraryReceiptDownload);

				const data = await refetchDocuments({
					params: {
						orderId: order.id,
						documentType: documentType ?? OrderDocumentType.All,
						travellerId: passengerId
					}
				});
				if (data?.data?.OrderDocument) {
					downloadPDF(data.data.OrderDocument, `${getOrderDocumentFileName(documentType)}.pdf`);
				}
			} catch (er) {
				if (er instanceof Error) {
					setError(er);
				}

				warningOpen();
			} finally {
				setNotLoading();
			}
		},
		[order.status]
	);

	return {
		handleDownload,
		document: data?.OrderDocument,
		loading,
		error: error || _error,
		warningIsOpen,
		warningClose
	};
};

const getOrderDocumentFileName = (type: OrderDocumentType) => {
	switch (type) {
		case OrderDocumentType.AeroexpressTicket:
			return 'aeroexpress';

		case OrderDocumentType.EMD:
			return 'emd';

		case OrderDocumentType.NASTicket:
			return 'airport_services';

		case OrderDocumentType.EveryTravelTicket:
			return 'tour_voucher';

		default:
			return 'tickets';
	}
};

export const useResendTickets = (orderId: string) => {
	const UNAUTHORIZED_CODE = '403';
	const CLOSE_POPUP_AFTER_SUCCESSFUL_RESEND_AFTER = 1500;
	const RETURN_DEFAULT_STATUS_AFTER_SUCCESSFUL_RESEND_AFTER = 2000;

	const [resendStage, setResendStage] = useState<ResendTicketsStages>(ResendTicketsStages.Default);
	const { isOpen, open, close } = useToggleable(false);

	const [resendEmail, { data, loading, error }] = useResendItineraryReceiptMutation();

	const checkIsUnauthorizedError = (error: ApolloError, resendStage: ResendTicketsStages) => {
		return resendStage === ResendTicketsStages.Error && error?.message.includes(UNAUTHORIZED_CODE);
	};

	const handleResendEmail = useCallback(
		async (email: string) => {
			setResendStage(ResendTicketsStages.Loading);

			return resendEmail({
				variables: {
					params: {
						email,
						orderId
					}
				}
			});
		},
		[isOpen, resendStage, orderId, resendEmail]
	);

	const handleWarningModalClose = () => {
		setResendStage(ResendTicketsStages.Default);
	};

	const isUnauthorizedError = useMemo(() => checkIsUnauthorizedError(error, resendStage), [error, resendStage]);

	useEffect(() => {
		const resendResult = data?.ResendItineraryReceipt;

		if (error || error?.graphQLErrors?.length) {
			setResendStage(ResendTicketsStages.Error);

			return;
		}

		if (!loading && !error && resendResult) {
			setResendStage(ResendTicketsStages.Success);
			setTimeout(close, CLOSE_POPUP_AFTER_SUCCESSFUL_RESEND_AFTER);

			setTimeout(() => {
				setResendStage(ResendTicketsStages.Default);
			}, RETURN_DEFAULT_STATUS_AFTER_SUCCESSFUL_RESEND_AFTER);
		}
	}, [data, loading, error]);

	return {
		openModal: open,
		closeModal: close,
		isModalOpen: isOpen,
		isUnauthorizedError,
		resendStage,
		handleWarningModalClose,
		handleResendEmail,
		loading,
		checkIsUnauthorizedError
	};
};

export const usePromoCodesData = () => {
	const [data, setData] = useState<{ promoCodesInfo?: OrderPromoCodeInfoFragment; error?: string }>(null);

	const [loading, setLoading] = useState(false);

	const { isOpen, setOpen, close } = useToggleable(false);

	return {
		data,
		setData,
		isOpen,
		setOpen,
		close,
		loading,
		setLoading
	};
};

const SYSTEM_PAGES = [
	'payment/successfull',
	'payment/failed',
	'refund',
	'refund-services',
	'exchange',
	'change-passenger-docs'
];

export const isCheckoutSystemPage = (pathname: string) => SYSTEM_PAGES.some(path => pathname.includes(path));

export const useSteps = () => {
	const order = useSelector(getOrder);
	const availableServices = useSelector(getAvailableServices),
		{
			global: { stepsAvailable, servicesAvailable },
			Engine: { servicesStepsOrder }
		} = useConfig();
	const isPaymentRequired = useSelector(getPaymentIsRequired),
		hasAvailableSeats = useSelector(getHasAvailableSeats),
		showServicesBeforeBooking = useSelector(getShowServicesBeforeBooking),
		isReadonly = useSelector(getIsReadonly),
		isTravellersDataFilled = useSelector(getIsTravellersDataFilled),
		isPriceFreezeProductSelected = useSelector(getIsPriceFreezeProductSelected),
		isBooked = useSelector(getIsBooked);
	const stepsRef = useRef<Step[]>();

	const location = useLocation(),
		match = useRouteMatch(),
		baseUrl = match.url;

	const history = useHistory();
	const stepRoutePath = useStepRoutePath(baseUrl);

	const isPassengerRoute = location.pathname === stepRoutePath('passengers');

	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
	const isRedirectFromMetasearch = useSelector(getIsRedirectFromMetasearch);

	const steps = useMemo(() => {
		const steps = getSteps({
			order,
			availableServices,
			isPaymentRequired,
			hasAvailableSeats,
			showServicesBeforeBooking,
			configStepAvailable: stepsAvailable,
			configServicesAvailable: servicesAvailable,
			servicesStepsOrder
		});

		if (isMobile && isRedirectFromMetasearch) {
			return removeStepsWhenRedirectFromMetasearch(steps);
		}

		if (!isPriceFreezeProductSelected && isBooked && isTravellersDataFilled && !isPassengerRoute) {
			return removeStepsWhenOrderHasPassengersData(steps);
		}

		return steps;
	}, [
		order,
		availableServices,
		isPaymentRequired,
		isReadonly,
		hasAvailableSeats,
		showServicesBeforeBooking,
		stepsAvailable,
		servicesStepsOrder,
		isMobile,
		isRedirectFromMetasearch,
		isPriceFreezeProductSelected,
		isBooked,
		isTravellersDataFilled,
		isPassengerRoute
	]);

	useEffect(() => {
		stepsRef.current = steps;
	}, [steps]);

	const getFirstAvailableStep = useCallback(() => {
		if (order.status !== OrderStatus.New) {
			return steps.find(step => step.type === StepsEnum.Order);
		}

		return steps.find(step => !step.hidden);
	}, [order.status, steps]);

	const currentStep = useMemo(() => {
		return steps.find(step => location.pathname === stepRoutePath(step.url));
	}, [steps, location.pathname, stepRoutePath]);

	const isSystemPage = isCheckoutSystemPage(location.pathname);

	if ((!currentStep || currentStep.hidden) && !isSystemPage) {
		const currentStepIndex = steps.findIndex(step => location.pathname === stepRoutePath(step.url)),
			nextAvailableStep =
				currentStepIndex === -1
					? getFirstAvailableStep()
					: steps.find((step, index) => !step.hidden && index > currentStepIndex);

		setTimeout(() => {
			const step = nextAvailableStep?.url ?? getFirstAvailableStep()?.url;

			let bookingURL = `/booking/${order.id}/${step}`;

			if (step === 'order') {
				bookingURL = getUrlWithServicesParams(bookingURL, location);
			}

			history.replace(bookingURL);
		});
	}

	const nextStep = useCallback(() => {
		const currentStepIndex = stepsRef.current.findIndex(step => location.pathname === stepRoutePath(step.url));
		const nextStep = stepsRef.current.find((step, index) => !step.hidden && index > currentStepIndex);

		if (nextStep) {
			history.push(`/booking/${order.id}/${nextStep.url}`);
		}
	}, [order.id, location.pathname, stepRoutePath, history]);

	const prevStep = useCallback(() => {
		const currentStepIndex = steps.findIndex(step => location.pathname === stepRoutePath(step.url));

		for (let i = currentStepIndex - 1; i >= 0; i--) {
			if (!steps[i].hidden) {
				history.push(`/booking/${order.id}/${steps[i].url}`);

				return;
			}
		}
	}, [order.id, steps, location.pathname, stepRoutePath, history]);

	const setStep = useCallback(
		(stepType: StepsEnum) => {
			const nextStepIndex = steps.findIndex(step => step.type === stepType);

			history.push(`/booking/${order.id}/${steps[nextStepIndex].url}`);
		},
		[order.id, steps, history]
	);

	return {
		steps: steps.filter(step => !step.hidden),
		currentStep,
		nextStep,
		prevStep,
		setStep
	};
};

export const useUpdateTravellerServices = () => {
	const dispatch = useDispatch();

	const order = useSelector(getOrder);
	const hasRequestedOrderServices = useSelector(getHasRequestedOrderServices);

	const [updateOrder] = useUpdateOrderMutation();

	const onUpdateOrder = useCallback(async () => {
		const { data } = await updateOrder({ variables: { id: order.id } });

		if (data?.UpdateOrder) {
			dispatch(fillOrder(data.UpdateOrder as CheckoutOrderFragment));
		}
	}, [order?.id, updateOrder]);

	useConditionalPolling(onUpdateOrder, hasRequestedOrderServices, {
		interval: 5000,
		progressiveIntervalPerQuery: true
	});
};

export const useQueryControlledServicesModal = () => {
	const history = useHistory();
	const location = useLocation();

	const existGdsServicesOrInsurances = useSelector(getExistGdsServicesOrInsurances);

	useEffect(() => {
		if (!existGdsServicesOrInsurances) {
			return;
		}

		const { serviceRfisc, openServiceType, serviceSubType, insuranceCode } = getQueryServicesParams(location);

		if (openServiceType) {
			const event = new CustomEvent<ServiceOpenEvent>(WEBSKY_SERVICE_OPEN_EVENT, {
				detail: {
					service: {
						rfisc: serviceRfisc,
						type: openServiceType,
						subType: serviceSubType,
						insuranceCode
					},
					meta: {
						custom_ecommerce_name: getQueryPopupGoal(
							openServiceType,
							serviceSubType,
							insuranceCode,
							serviceRfisc
						)
					},
					callback: async () => {}
				}
			});

			history.replace({ search: '' });
			setTimeout(() => document.dispatchEvent(event));
		}
	}, [existGdsServicesOrInsurances, location]);
};

export const useQueryEventToggleable = ({
	serviceTypes,
	serviceRfisc,
	serviceSubTypes,
	insuranceCodes,
	onOpen
}: {
	serviceTypes: CombinedSelectedServiceType[];
	serviceRfisc?: string[];
	serviceSubTypes?: CombinedSelectedServiceSubType[];
	insuranceCodes?: string[];
	onOpen: () => void;
}) => {
	const onEventHandler = useCallback(
		(event: CustomEvent<ServiceOpenEvent>) => {
			const { type, rfisc, subType, insuranceCode } = event.detail.service;

			if (!serviceTypes.includes(type)) {
				return;
			}

			if (
				(rfisc || subType || insuranceCode) &&
				(serviceRfisc || serviceSubTypes || insuranceCodes || []).length
			) {
				const isIncludeRfisc = serviceRfisc.includes(rfisc);
				const isIncludeSubType = serviceSubTypes.includes(subType);
				const isIncludeInsuranceCode = insuranceCodes.includes(insuranceCode);

				if (!isIncludeRfisc && !isIncludeSubType && !isIncludeInsuranceCode) {
					return;
				}
			}

			onOpen();
		},
		[serviceTypes, serviceRfisc, serviceSubTypes, insuranceCodes, onOpen]
	);

	useEffect(() => {
		document.addEventListener(WEBSKY_SERVICE_OPEN_EVENT, onEventHandler as EventListener);

		return () => document.removeEventListener(WEBSKY_SERVICE_OPEN_EVENT, onEventHandler as EventListener);
	}, []);
};

export const useGdsServiceEventToggleable = ({
	services,
	serviceTypes,
	serviceSubTypes = [],
	onOpen
}: {
	services: GdsServiceFragment[];
	serviceTypes: CombinedSelectedServiceType[];
	serviceSubTypes?: CombinedSelectedServiceSubType[];
	onOpen: () => void;
}) => {
	const { availableServiceRfisc, availableServiceSubTypes } = useMemo(() => {
		const serviceRfiscSet = new Set<string>();
		const serviceSubTypeSet = new Set<CombinedSelectedServiceSubType>(serviceSubTypes);

		services.forEach(service => {
			serviceRfiscSet.add(service.rfisc);

			if ((service as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment)?.baggageType) {
				// BaggageService
				const baggageService = service as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment;

				serviceSubTypeSet.add(baggageService.baggageType);
			} else if ((service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment)?.subType) {
				// Vip service
				const vipService = service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment;

				serviceSubTypeSet.add(vipService.subType);
			}
		});

		return {
			availableServiceRfisc: [...serviceRfiscSet],
			availableServiceSubTypes: [...serviceSubTypeSet]
		};
	}, [services, serviceSubTypes]);

	useQueryEventToggleable({
		serviceTypes,
		serviceRfisc: availableServiceRfisc,
		serviceSubTypes: availableServiceSubTypes,
		onOpen
	});
};

export const useNasServiceEventToggleable = ({
	services,
	serviceTypes,
	onOpen
}: {
	services: NASProductFragment[];
	serviceTypes: CombinedSelectedServiceType[];
	onOpen: () => void;
}) => {
	const availableServiceSubTypes = useMemo(() => {
		const serviceSubTypeSet = new Set<NASProductType>();
		services.forEach(service => serviceSubTypeSet.add(service.type));

		return [...serviceSubTypeSet];
	}, [services]);

	useQueryEventToggleable({
		serviceTypes,
		serviceSubTypes: availableServiceSubTypes,
		onOpen
	});
};

export const useQueryParamsOrderUpdate = () => {
	const history = useHistory();
	const location = useLocation();
	const dispatch = useDispatch();
	const order = useSelector(getOrder);

	const orderRef = useRef(order);
	orderRef.current = order;

	const [updateOrder] = useUpdateOrderMutation();
	const [loadAdditionalServices] = useAdditionalServicesLazyQuery({
		fetchPolicy: 'no-cache',
		onCompleted: data => {
			if (data?.AdditionalServices && orderRef.current) {
				dispatch(
					fillOrder({
						...orderRef.current,
						additionalServices: data.AdditionalServices
					})
				);
			}
		}
	});

	useEffect(() => {
		const { updateOrder: shouldUpdateOrder, updateServices: shouldUpdateServices } = getQueryServicesParams(
			location
		);

		if (!order?.id || (!shouldUpdateOrder && !shouldUpdateServices)) {
			return;
		}

		const runMutations = async () => {
			if (shouldUpdateOrder) {
				const { data } = await updateOrder({ variables: { id: order.id } });

				if (data?.UpdateOrder) {
					dispatch(fillOrder(data.UpdateOrder as CheckoutOrderFragment));
				}
			}

			if (shouldUpdateServices) {
				loadAdditionalServices({ variables: { id: order.id } });
			}

			history.replace({ search: '' });
		};

		runMutations();
	}, [order?.id, location.search, updateOrder, loadAdditionalServices, dispatch, history]);
};

export const useCheckoutPaymentFailedHandlers = ({
	setStep,
	setPaymentResults
}: {
	setStep: (step: StepType) => void;
	setPaymentResults: Dispatch<SetStateAction<PaymentResultStatus | null>>;
}) => {
	const dispatch = useDispatch();
	const orderId = useSelector(getOrderId);
	const isBlockedForPayment = useSelector(getIsOrderBlockedForPayment);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<NormalizedError | null>(null);
	/* Инкремент в onErrorConfirm пересоздаёт onFlowStart и перезапускает flow в PaymentResultFlow */
	const [flowStartKey, setFlowStartKey] = useState(0);

	const { refetch } = useOrderQuery({ skip: true, fetchPolicy: 'no-cache' });

	const [resetOrderPaymentBlock] = useResetOrderPaymentBlockMutation();

	const resetBlock = useCallback(
		async (id: string) => {
			await resetOrderPaymentBlock({ variables: { id } });
		},
		[resetOrderPaymentBlock]
	);

	const refetchOrder = useCallback(
		async (id: string) => {
			const { data } = await refetch({ id });

			return data?.Order ?? null;
		},
		[refetch]
	);

	const applyOrder = useCallback(
		(order: CheckoutOrderFragment) => {
			dispatch(fillOrder(order));
		},
		[dispatch]
	);

	const handleSetError = useCallback((error: unknown) => {
		if (error === null) {
			setError(null);

			return;
		}

		setError({
			details: [{ label: '', value: getErrorMessage(error) }]
		});
	}, []);

	const { resetPaymentBlockAndRefetchOrder } = useResetPaymentBlock<CheckoutOrderFragment>({
		orderId,
		isBlockedForPayment,
		resetBlock,
		refetchOrder,
		applyOrder,
		setLoading,
		setError: handleSetError
	});

	const onFlowStart = useCallback(resetPaymentBlockAndRefetchOrder, [flowStartKey, resetPaymentBlockAndRefetchOrder]);

	const baseBack = useCallback(
		async (step: StepType) => {
			const isSuccess = await resetPaymentBlockAndRefetchOrder();

			if (isSuccess) {
				setStep(step);
				setPaymentResults(null);
			}
		},
		[setStep, resetPaymentBlockAndRefetchOrder, setPaymentResults]
	);

	const onMount = useCallback(() => {
		setPaymentResults(PaymentResultStatus.Fail);
		reachGoal(OrderGoal.PaymentFailed);
	}, [reachGoal, setPaymentResults]);

	const goToPaymentAfterFailedPayment = useCallback(() => {
		baseBack(StepType.Payment);
	}, [baseBack]);

	const goToOrderAfterFailedPayment = useCallback(() => {
		baseBack(StepType.Order);
	}, [baseBack]);

	const clearError = useCallback(() => {
		setError(null);
	}, []);

	const onErrorConfirm = useCallback(() => {
		setError(null);

		setFlowStartKey(prev => prev + 1);
	}, []);

	const onErrorCancel = useCallback(() => {
		setError(null);

		setPaymentResults(null);
		setStep(StepType.Order);

		window.location.reload();
	}, [setStep, setPaymentResults]);

	return {
		isLoading: loading,
		error,
		clearError,
		onMount,
		onFlowStart,
		goToPaymentAfterFailedPayment,
		goToOrderAfterFailedPayment,
		onErrorConfirm,
		onErrorCancel,
		onErrorClose: onErrorCancel
	};
};

export const useCheckoutPaymentSuccessHandlers = ({
	setStep,
	baseUrl,
	setPaymentResults
}: {
	setStep: (step: StepType) => void;
	baseUrl: string;
	setPaymentResults: Dispatch<SetStateAction<PaymentResultStatus | null>>;
}) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const orderId = useSelector(getOrderId);
	const isTravellersDataFilled = useSelector(getIsTravellersDataFilled);

	const [error, setError] = useState<NormalizedError | null>(null);
	const [flowStartKey, setFlowStartKey] = useState(0);

	const { isPolling, startPolling, stopPolling } = usePaymentPolling<CheckoutOrderFragment>();

	const { refetch } = useOrderQuery({
		skip: true,
		fetchPolicy: 'no-cache',
		variables: { id: orderId }
	});

	const refetchOrder = useCallback(async () => {
		const { data } = await refetch({ id: orderId });

		return data?.Order ?? null;
	}, [orderId, refetch]);

	const onFlowStart = useCallback((): Promise<boolean> => {
		return new Promise(resolve => {
			startPolling({
				fetcher: refetchOrder,
				maxAttempts: 10,
				shouldStop: order => {
					const isFullyPayedOrder =
						order.status === OrderStatus.Confirmed && order.paymentStatus === PaymentStatusEnum.Full;

					const isPayedPriceFreeze =
						order.isPriceFreezeProductSelected &&
						!isTravellersDataFilled &&
						order.paymentStatus === PaymentStatusEnum.Part;

					const orderIsCancelled = order.status === OrderStatus.Cancelled;

					return isFullyPayedOrder || isPayedPriceFreeze || orderIsCancelled;
				},
				onSuccess: order => {
					dispatch(fillOrder(order));
					resolve(true);
				},
				onTimeout: () => {
					setPaymentResults(null);
					history.push(`${baseUrl}/payment/failed`);
					resolve(false);
				},
				onError: error => {
					setError({
						details: [{ label: '', value: getErrorMessage(error) }]
					});
					resolve(false);
				}
			});
		});
	}, [
		flowStartKey,
		baseUrl,
		dispatch,
		history,
		isTravellersDataFilled,
		refetchOrder,
		setPaymentResults,
		startPolling
	]);

	const onMount = useCallback(() => {
		setPaymentResults(PaymentResultStatus.Success);

		dispatch(analyzePurchasedProductsAction());
		reachGoal(OrderGoal.Paid);
	}, [dispatch, reachGoal, setPaymentResults]);

	const goToOrderAfterSuccessPayment = useCallback(() => {
		setStep(StepType.Order);

		setPaymentResults(null);
	}, [setPaymentResults, setStep]);

	const clearError = useCallback(() => {
		setError(null);
	}, []);

	const onErrorConfirm = useCallback(() => {
		setError(null);

		setFlowStartKey(prev => prev + 1);
	}, []);

	const onErrorCancel = useCallback(() => {
		setError(null);

		goToOrderAfterSuccessPayment();

		window.location.reload();
	}, [goToOrderAfterSuccessPayment]);

	return {
		isLoading: isPolling,
		onMount,
		onFlowStart,
		onFlowAbort: stopPolling,
		error,
		clearError,
		onErrorConfirm,
		onErrorCancel,
		onErrorClose: onErrorCancel,
		goToOrderAfterSuccessPayment
	};
};
